import React, { useMemo } from 'react';
import { useFormik } from 'formik';
import Select from 'react-select';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useTheme } from '@/app/contexts/ThemeContext';
import type { CrawlProgress, SearchSubmitParams } from '../types/keyword.types';

interface LanguageDict {
  [key: string]: string;
}

interface BrandData {
  fa: string;
  en: string;
  icon: string;
  image: string | null;
}

interface BrandsDict {
  [key: string]: BrandData;
}

interface SelectOption {
  value: string;
  label: string;
}

interface BrandOption extends SelectOption {
  icon: string;
  image: string | null;
}

interface FormValues {
  keyword: string;
  brands: string[];
  language: string;
  country: string;
}

interface FormErrors {
  keyword?: string;
}

interface SearchFormProps {
  onSearch: (params: SearchSubmitParams) => void;
  isSearching?: boolean;
  searchProgress?: CrawlProgress | null;
  statusMessage?: string | null;
  keywordsFoundCount?: number;
  compact?: boolean;
}

const languages: LanguageDict = {
  "de": "آلمانی",
  "ur": "اردو",
  "et": "استونیایی",
  "sk": "اسلواکی",
  "sl": "اسلوونیایی",
  "es": "اسپانیایی",
  "id": "اندونزیایی",
  "en": "انگلیسی",
  "uk": "اوکراینی",
  "it": "ایتالیایی",
  "is": "ایسلندی",
  "bg": "بلغاری",
  "bn": "بنگالی",
  "ta": "تامیل",
  "th": "تایلندی",
  "tr": "ترکی",
  "te": "تلوگو",
  "da": "دانمارکی",
  "ru": "روسی",
  "ro": "رومانیایی",
  "zh-CN": "زبان چینی",
  "sv": "سوئدی",
  "sr": "صربی",
  "iw": "عبری",
  "ar": "عربی",
  "fa": "فارسی",
  "fr": "فرانسوی",
  "fi": "فنلاندی",
  "tl": "فیلیپینی",
  "lv": "لتونی",
  "pl": "لهستانی",
  "lt": "لیتوانیایی",
  "ml": "مالایم",
  "ms": "مالایی",
  "hu": "مجارستانی",
  "mr": "مراتی",
  "no": "نروژی",
  "nl": "هلندی",
  "hi": "هندی",
  "vi": "ویتنامی",
  "pt": "پرتغالی",
  "pa": "پنجابی",
  "cs": "چکی",
  "zh-TW": "چینی (سنتی)",
  "ja": "ژاپنی",
  "ca": "کاتالان",
  "kn": "کانادا",
  "ko": "کره ای",
  "hr": "کروات",
  "gu": "گجراتی",
  "el": "یونانی"
};

const locations: LanguageDict = {
  "az": "آذربایجان",
  "ar": "آرژانتین",
  "za": "آفریقای جنوبی",
  "al": "آلبانی",
  "de": "آلمان",
  "us": "آمریکا",
  "ag": "آنتیگوا و باربودا",
  "ad": "آندورا",
  "ao": "آنگولا",
  "ai": "آنگویلا",
  "at": "اتریش",
  "et": "اتیوپی",
  "jo": "اردن",
  "am": "ارمنستان",
  "uy": "اروگوئه",
  "er": "اریتره",
  "uz": "ازبکستان",
  "au": "استرالیا",
  "ee": "استونی",
  "sk": "اسلواکی",
  "si": "اسلوونی",
  "es": "اسپانیا",
  "af": "افغانستان",
  "dz": "الجزایر",
  "sv": "السالوادور",
  "ae": "امارات متحده عربی",
  "id": "اندونزی",
  "gb": "انگلستان",
  "ua": "اوکراین",
  "ug": "اوگاندا",
  "ec": "اکوادور",
  "it": "ایتالیا",
  "ir": "ایران",
  "ie": "ایرلند",
  "is": "ایسلند",
  "bb": "باربادوس",
  "bs": "باهاما",
  "bh": "بحرین",
  "br": "برزیل",
  "bn": "برونئی",
  "by": "بلاروس",
  "bg": "بلغارستان",
  "be": "بلژیک",
  "bz": "بلیز",
  "bd": "بنگلادش",
  "bj": "بنین",
  "bt": "بوتان",
  "bw": "بوتسوانا",
  "bi": "بوروندی",
  "bf": "بورکینافاسو",
  "ba": "بوسنی و هرزگوین",
  "bo": "بولیوی",
  "tj": "تاجیکستان",
  "tz": "تانزانیا",
  "th": "تایلند",
  "tw": "تایوان",
  "tm": "ترکمنستان",
  "tr": "ترکیه",
  "tt": "ترینیداد و توباگو",
  "tn": "تونس",
  "to": "تونگا",
  "tv": "تووالو",
  "tk": "توکلائو",
  "tg": "توگو",
  "tl": "تیمور شرقی",
  "jm": "جامائیکا",
  "gi": "جبل الطارق",
  "je": "جرزی",
  "tc": "جزایر تورکس و کایکوس",
  "sb": "جزایر سلیمان",
  "fo": "جزایر فارو",
  "fk": "جزایر فالکلند [جزیره مالویناس]",
  "mh": "جزایر مارشال",
  "mp": "جزایر ماریانای شمالی",
  "vi": "جزایر ویرجین ایالات متحده",
  "pn": "جزایر پیتکرن",
  "um": "جزایر کوچک دورافتاده ایالات متحده",
  "ck": "جزایر کوک",
  "cc": "جزایر کوکوس [کیلینگ]",
  "im": "جزیره من",
  "nf": "جزیره نورفولک",
  "hm": "جزیره هرد و جزایر مک دونالد",
  "cx": "جزیره کریسمس",
  "cf": "جمهوری آفریقای مرکزی",
  "do": "جمهوری دومینیکن",
  "cz": "جمهوری چک",
  "aq": "جنوبگان",
  "gs": "جورجیا جنوبی و جزایر ساندویچ جنوبی",
  "dj": "جیبوتی",
  "dk": "دانمارک",
  "dm": "دومینیکا",
  "rw": "رواندا",
  "ru": "روسیه",
  "ro": "رومانی",
  "zm": "زامبیا",
  "zw": "زیمبابوه",
  "st": "سائوتومه و پرنسیپ",
  "ci": "ساحل عاج",
  "ws": "ساموآ",
  "as": "ساموآی آمریکایی",
  "tf": "سرزمین های جنوبی فرانسه",
  "lk": "سری لانکا",
  "sm": "سن مارینو",
  "lc": "سنت لوسیا",
  "sh": "سنت هلنا",
  "vc": "سنت وینسنت و گرنادین ها",
  "pm": "سنت پیر و میکلون",
  "kn": "سنت کیتس و نویس",
  "sn": "سنگال",
  "sg": "سنگاپور",
  "se": "سوئد",
  "ch": "سوئیس",
  "sz": "سوازیلند",
  "sj": "سوالبارد و یان ماین",
  "sd": "سودان",
  "sr": "سورینام",
  "sy": "سوریه",
  "so": "سومالی",
  "sl": "سیرا لئون",
  "sc": "سیشل",
  "va": "شهر واتیکان",
  "cl": "شیلی",
  "eh": "صحرای غربی",
  "rs": "صربستان",
  "iq": "عراق",
  "sa": "عربستان سعودی",
  "om": "عمان",
  "gh": "غنا",
  "fr": "فرانسه",
  "ps": "فلسطین",
  "fi": "فنلاند",
  "fj": "فیجی",
  "ph": "فیلیپین",
  "cy": "قبرس",
  "kg": "قرقیزستان",
  "kz": "قزاقستان",
  "qa": "قطر",
  "io": "قلمرو اقیانوس هند بریتانیا",
  "la": "لائوس",
  "lb": "لبنان",
  "lv": "لتونی",
  "ls": "لسوتو",
  "pl": "لهستان",
  "lu": "لوکزامبورگ",
  "lr": "لیبریا",
  "ly": "لیبی",
  "lt": "لیتوانی",
  "li": "لیختن اشتاین",
  "mg": "ماداگاسکار",
  "mw": "مالاوی",
  "mt": "مالت",
  "mv": "مالدیو",
  "my": "مالزی",
  "ml": "مالی",
  "mo": "ماکائو",
  "hu": "مجارستان",
  "ma": "مراکش",
  "eg": "مصر",
  "mn": "مغولستان",
  "mk": "مقدونیه [FYROM]",
  "mr": "موریتانی",
  "mu": "موریس",
  "mz": "موزامبیک",
  "md": "مولداوی",
  "mc": "موناکو",
  "ms": "مونتسرات",
  "me": "مونته نگرو",
  "mx": "مکزیک",
  "mm": "میانمار [برمه]",
  "fm": "میکرونزی",
  "nr": "نائورو",
  "na": "نامیبیا",
  "no": "نروژ",
  "np": "نپال",
  "ne": "نیجر",
  "ng": "نیجریه",
  "nu": "نیوئه",
  "nz": "نیوزلند",
  "ni": "نیکاراگوئه",
  "ht": "هائیتی",
  "nl": "هلند",
  "in": "هند",
  "hn": "هندوراس",
  "hk": "هنگ کنگ",
  "wf": "والیس و فوتونا",
  "vu": "وانواتو",
  "ve": "ونزوئلا",
  "vn": "ویتنام",
  "py": "پاراگوئه",
  "pw": "پالائو",
  "pa": "پاناما",
  "pg": "پاپوآ گینه نو",
  "pk": "پاکستان",
  "pt": "پرتغال",
  "pe": "پرو",
  "pf": "پلینزی فرانسه",
  "pr": "پورتوریکو",
  "td": "چاد",
  "cn": "چین",
  "jp": "ژاپن",
  "cr": "کاستاریکا",
  "nc": "کالدونیای جدید",
  "kh": "کامبوج",
  "cm": "کامرون",
  "ca": "کانادا",
  "kr": "کره جنوبی",
  "kp": "کره شمالی",
  "hr": "کرواسی",
  "co": "کلمبیا",
  "cd": "کنگو [DRC]",
  "cg": "کنگو [جمهوری]",
  "ke": "کنیا",
  "cu": "کوبا",
  "xk": "کوزوو",
  "km": "کومور",
  "kw": "کویت",
  "ki": "کیریباتی",
  "cv": "کیپ ورد",
  "ga": "گابن",
  "gm": "گامبیا",
  "gd": "گرانادا",
  "ge": "گرجستان",
  "gg": "گرنزی",
  "gl": "گرینلند",
  "gt": "گواتمالا",
  "gp": "گوادلوپ",
  "gu": "گوام",
  "gy": "گویان",
  "gf": "گویان فرانسه",
  "gn": "گینه",
  "gq": "گینه استوایی",
  "gw": "گینه بیسائو",
  "ye": "یمن",
  "gr": "یونان"
};

// Brands/Sources configuration
const brands: BrandsDict = {
  "smart": {
    fa: "هوشمند",
    en: "Smart",
    icon: "🧠",
    image: null
  },
  "google": {
    fa: "گوگل",
    en: "Google",
    icon: "🔍",
    image: "https://www.google.com/s2/favicons?domain=google.com&sz=32"
  },
  "bing": {
    fa: "بینگ",
    en: "Bing",
    icon: "🔎",
    image: "https://www.google.com/s2/favicons?domain=bing.com&sz=32"
  },
  "youtube": {
    fa: "یوتیوب",
    en: "YouTube",
    icon: "▶️",
    image: "https://www.google.com/s2/favicons?domain=youtube.com&sz=32"
  },
  "aparat": {
    fa: "آپارات",
    en: "Aparat",
    icon: "📹",
    image: "https://www.google.com/s2/favicons?domain=aparat.com&sz=32"
  },
  "digikala": {
    fa: "دیجی‌کالا",
    en: "Digikala",
    icon: "🛒",
    image: "https://www.google.com/s2/favicons?domain=digikala.com&sz=32"
  },
  "divar": {
    fa: "دیوار",
    en: "Divar",
    icon: "🏠",
    image: "https://www.google.com/s2/favicons?domain=divar.ir&sz=32"
  },
  "ai": {
    fa: "هوش مصنوعی",
    en: "AI",
    icon: "🤖",
    image: null
  }
};

// English translations for languages
const languagesEn: LanguageDict = {
  "de": "German",
  "ur": "Urdu",
  "et": "Estonian",
  "sk": "Slovak",
  "sl": "Slovenian",
  "es": "Spanish",
  "id": "Indonesian",
  "en": "English",
  "uk": "Ukrainian",
  "it": "Italian",
  "is": "Icelandic",
  "bg": "Bulgarian",
  "bn": "Bengali",
  "ta": "Tamil",
  "th": "Thai",
  "tr": "Turkish",
  "te": "Telugu",
  "da": "Danish",
  "ru": "Russian",
  "ro": "Romanian",
  "zh-CN": "Chinese",
  "sv": "Swedish",
  "sr": "Serbian",
  "iw": "Hebrew",
  "ar": "Arabic",
  "fa": "Persian",
  "fr": "French",
  "fi": "Finnish",
  "tl": "Filipino",
  "lv": "Latvian",
  "pl": "Polish",
  "lt": "Lithuanian",
  "ml": "Malayalam",
  "ms": "Malay",
  "hu": "Hungarian",
  "mr": "Marathi",
  "no": "Norwegian",
  "nl": "Dutch",
  "hi": "Hindi",
  "vi": "Vietnamese",
  "pt": "Portuguese",
  "pa": "Punjabi",
  "cs": "Czech",
  "zh-TW": "Chinese (Traditional)",
  "ja": "Japanese",
  "ca": "Catalan",
  "kn": "Kannada",
  "ko": "Korean",
  "hr": "Croatian",
  "gu": "Gujarati",
  "el": "Greek"
};

// English translations for countries
const locationsEn: LanguageDict = {
  "az": "Azerbaijan",
  "ar": "Argentina",
  "za": "South Africa",
  "al": "Albania",
  "de": "Germany",
  "us": "United States",
  "ag": "Antigua and Barbuda",
  "ad": "Andorra",
  "ao": "Angola",
  "ai": "Anguilla",
  "at": "Austria",
  "et": "Ethiopia",
  "jo": "Jordan",
  "am": "Armenia",
  "uy": "Uruguay",
  "er": "Eritrea",
  "uz": "Uzbekistan",
  "au": "Australia",
  "ee": "Estonia",
  "sk": "Slovakia",
  "si": "Slovenia",
  "es": "Spain",
  "af": "Afghanistan",
  "dz": "Algeria",
  "sv": "El Salvador",
  "ae": "United Arab Emirates",
  "id": "Indonesia",
  "gb": "United Kingdom",
  "ua": "Ukraine",
  "ug": "Uganda",
  "ec": "Ecuador",
  "it": "Italy",
  "ir": "Iran",
  "ie": "Ireland",
  "is": "Iceland",
  "bb": "Barbados",
  "bs": "Bahamas",
  "bh": "Bahrain",
  "br": "Brazil",
  "bn": "Brunei",
  "by": "Belarus",
  "bg": "Bulgaria",
  "be": "Belgium",
  "bz": "Belize",
  "bd": "Bangladesh",
  "bj": "Benin",
  "bt": "Bhutan",
  "bw": "Botswana",
  "bi": "Burundi",
  "bf": "Burkina Faso",
  "ba": "Bosnia and Herzegovina",
  "bo": "Bolivia",
  "tj": "Tajikistan",
  "tz": "Tanzania",
  "th": "Thailand",
  "tw": "Taiwan",
  "tm": "Turkmenistan",
  "tr": "Turkey",
  "tt": "Trinidad and Tobago",
  "tn": "Tunisia",
  "to": "Tonga",
  "tv": "Tuvalu",
  "tk": "Tokelau",
  "tg": "Togo",
  "tl": "East Timor",
  "jm": "Jamaica",
  "gi": "Gibraltar",
  "je": "Jersey",
  "tc": "Turks and Caicos Islands",
  "sb": "Solomon Islands",
  "fo": "Faroe Islands",
  "fk": "Falkland Islands [Malvinas]",
  "mh": "Marshall Islands",
  "mp": "Northern Mariana Islands",
  "vi": "U.S. Virgin Islands",
  "pn": "Pitcairn Islands",
  "um": "U.S. Minor Outlying Islands",
  "ck": "Cook Islands",
  "cc": "Cocos [Keeling] Islands",
  "im": "Isle of Man",
  "nf": "Norfolk Island",
  "hm": "Heard Island and McDonald Islands",
  "cx": "Christmas Island",
  "cf": "Central African Republic",
  "do": "Dominican Republic",
  "cz": "Czech Republic",
  "aq": "Antarctica",
  "gs": "South Georgia and the South Sandwich Islands",
  "dj": "Djibouti",
  "dk": "Denmark",
  "dm": "Dominica",
  "rw": "Rwanda",
  "ru": "Russia",
  "ro": "Romania",
  "zm": "Zambia",
  "zw": "Zimbabwe",
  "st": "São Tomé and Príncipe",
  "ci": "Ivory Coast",
  "ws": "Samoa",
  "as": "American Samoa",
  "tf": "French Southern Territories",
  "lk": "Sri Lanka",
  "sm": "San Marino",
  "lc": "Saint Lucia",
  "sh": "Saint Helena",
  "vc": "Saint Vincent and the Grenadines",
  "pm": "Saint Pierre and Miquelon",
  "kn": "Saint Kitts and Nevis",
  "sn": "Senegal",
  "sg": "Singapore",
  "se": "Sweden",
  "ch": "Switzerland",
  "sz": "Eswatini",
  "sj": "Svalbard and Jan Mayen",
  "sd": "Sudan",
  "sr": "Suriname",
  "sy": "Syria",
  "so": "Somalia",
  "sl": "Sierra Leone",
  "sc": "Seychelles",
  "va": "Vatican City",
  "cl": "Chile",
  "eh": "Western Sahara",
  "rs": "Serbia",
  "iq": "Iraq",
  "sa": "Saudi Arabia",
  "om": "Oman",
  "gh": "Ghana",
  "fr": "France",
  "ps": "Palestine",
  "fi": "Finland",
  "fj": "Fiji",
  "ph": "Philippines",
  "cy": "Cyprus",
  "kg": "Kyrgyzstan",
  "kz": "Kazakhstan",
  "qa": "Qatar",
  "io": "British Indian Ocean Territory",
  "la": "Laos",
  "lb": "Lebanon",
  "lv": "Latvia",
  "ls": "Lesotho",
  "pl": "Poland",
  "lu": "Luxembourg",
  "lr": "Liberia",
  "ly": "Libya",
  "lt": "Lithuania",
  "li": "Liechtenstein",
  "mg": "Madagascar",
  "mw": "Malawi",
  "mt": "Malta",
  "mv": "Maldives",
  "my": "Malaysia",
  "ml": "Mali",
  "mo": "Macao",
  "hu": "Hungary",
  "ma": "Morocco",
  "eg": "Egypt",
  "mn": "Mongolia",
  "mk": "North Macedonia",
  "mr": "Mauritania",
  "mu": "Mauritius",
  "mz": "Mozambique",
  "md": "Moldova",
  "mc": "Monaco",
  "ms": "Montserrat",
  "me": "Montenegro",
  "mx": "Mexico",
  "mm": "Myanmar [Burma]",
  "fm": "Micronesia",
  "nr": "Nauru",
  "na": "Namibia",
  "no": "Norway",
  "np": "Nepal",
  "ne": "Niger",
  "ng": "Nigeria",
  "nu": "Niue",
  "nz": "New Zealand",
  "ni": "Nicaragua",
  "ht": "Haiti",
  "nl": "Netherlands",
  "in": "India",
  "hn": "Honduras",
  "hk": "Hong Kong",
  "wf": "Wallis and Futuna",
  "vu": "Vanuatu",
  "ve": "Venezuela",
  "vn": "Vietnam",
  "py": "Paraguay",
  "pw": "Palau",
  "pa": "Panama",
  "pg": "Papua New Guinea",
  "pk": "Pakistan",
  "pt": "Portugal",
  "pe": "Peru",
  "pf": "French Polynesia",
  "pr": "Puerto Rico",
  "td": "Chad",
  "cn": "China",
  "jp": "Japan",
  "cr": "Costa Rica",
  "nc": "New Caledonia",
  "kh": "Cambodia",
  "cm": "Cameroon",
  "ca": "Canada",
  "kr": "South Korea",
  "kp": "North Korea",
  "hr": "Croatia",
  "co": "Colombia",
  "cd": "Congo [DRC]",
  "cg": "Congo [Republic]",
  "ke": "Kenya",
  "cu": "Cuba",
  "xk": "Kosovo",
  "km": "Comoros",
  "kw": "Kuwait",
  "ki": "Kiribati",
  "cv": "Cape Verde",
  "ga": "Gabon",
  "gm": "Gambia",
  "gd": "Grenada",
  "ge": "Georgia",
  "gg": "Guernsey",
  "gl": "Greenland",
  "gt": "Guatemala",
  "gp": "Guadeloupe",
  "gu": "Guam",
  "gy": "Guyana",
  "gf": "French Guiana",
  "gn": "Guinea",
  "gq": "Equatorial Guinea",
  "gw": "Guinea-Bissau",
  "ye": "Yemen",
  "gr": "Greece"
};

const SearchForm: React.FC<SearchFormProps> = ({
  onSearch,
  isSearching = false,
  searchProgress = null,
  statusMessage = null,
  keywordsFoundCount = 0,
  compact = false,
}) => {
  const { translations, isRTL, language: currentLanguage } = useLanguage();
  const { theme } = useTheme();

  // Convert languages object to options array based on current language
  const languageOptions = useMemo<SelectOption[]>(() => {
    const langData = currentLanguage === 'en' ? languagesEn : languages;
    return Object.entries(langData).map(([value, label]) => ({
      value,
      label,
    }));
  }, [currentLanguage]);

  // Convert locations object to options array based on current language
  const countryOptions = useMemo<SelectOption[]>(() => {
    const locData = currentLanguage === 'en' ? locationsEn : locations;
    return Object.entries(locData).map(([value, label]) => ({
      value,
      label,
    }));
  }, [currentLanguage]);

  // Convert brands object to options array based on current language
  const brandOptions = useMemo<BrandOption[]>(() => {
    return Object.entries(brands).map(([value, data]) => ({
      value,
      label: data[currentLanguage === 'en' ? 'en' : 'fa'],
      icon: data.icon,
      image: data.image,
    }));
  }, [currentLanguage]);

  const formik = useFormik<FormValues>({
    initialValues: {
      keyword: '',
      brands: ['google'],
      language: 'fa',
      country: 'ir',
    },
    validate: (values): FormErrors => {
      const errors: FormErrors = {};
      // Check word count
      if (values.keyword) {
        const wordCount = values.keyword.trim().split(/\s+/).filter(word => word.length > 0).length;
        if (wordCount > 3) {
          errors.keyword = currentLanguage === 'fa' 
            ? 'برای گرفتن نتیجه بهتر تعداد کلمات جستجو بهتره از سه تا کمتر باشد.'
            : 'For better results, the number of search keywords should be less than three.';
        }
      }

      return errors;
    },
    onSubmit: (values) => {
      if (!values.keyword.trim()) {
        formik.setFieldError(
          'keyword',
          currentLanguage === 'fa' ? 'لطفاً کلمه کلیدی را وارد کنید.' : 'Please enter a keyword.'
        );
        return;
      }

      onSearch({
        keyword: values.keyword.trim(),
        language: values.language,
        country: values.country,
      });
    },
  });

  // Handle keyword input change with word count validation
  const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    formik.handleChange(e);
    
    // Check word count in real-time
    if (value) {
      const wordCount = value.trim().split(/\s+/).filter(word => word.length > 0).length;
      if (wordCount > 3) {
        // Set error if more than 3 words
        formik.setFieldError('keyword', currentLanguage === 'fa' 
          ? 'برای گرفتن نتیجه بهتر تعداد کلمات جستجو بهتره از سه تا کمتر باشد.'
          : 'For better results, the number of search keywords should be less than three.');
      } else {
        // Clear error if valid
        formik.setFieldError('keyword', undefined);
      }
    } else {
      // Clear error if empty
      formik.setFieldError('keyword', undefined);
    }
  };

  // Custom styles for react-select
  const customStyles = useMemo(() => ({
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
      borderColor: theme === 'dark' ? '#4b5563' : '#d1d5db',
      color: theme === 'dark' ? '#f3f4f6' : '#111827',
      minHeight: '42px',
      '&:hover': {
        borderColor: '#6e46e5',
      },
      boxShadow: state.isFocused ? '0 0 0 2px rgba(110, 70, 229, 0.2)' : 'none',
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
      maxHeight: '300px',
      zIndex: 9999,
    }),
    menuList: (base: any) => ({
      ...base,
      maxHeight: '300px',
      padding: '4px',
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#6e46e5'
        : state.isFocused
        ? theme === 'dark' ? '#374151' : '#f3f4f6'
        : 'transparent',
      color: state.isSelected
        ? '#ffffff'
        : theme === 'dark' ? '#f3f4f6' : '#111827',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#6e46e5',
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: theme === 'dark' ? '#f3f4f6' : '#111827',
    }),
    input: (base: any) => ({
      ...base,
      color: theme === 'dark' ? '#f3f4f6' : '#111827',
    }),
    placeholder: (base: any) => ({
      ...base,
      color: theme === 'dark' ? '#9ca3af' : '#6b7280',
    }),
  }), [theme]);

  return (
    <div
      className={`bg-white dark:bg-gray-900 rounded-lg shadow-lg transition-all duration-500 ease-in-out ${
        compact ? 'p-5 md:p-6 mb-4' : 'p-8 md:p-10 mb-0'
      }`}
    >
      <form onSubmit={formik.handleSubmit} className={`transition-all duration-500 ease-in-out ${compact ? 'space-y-4' : 'space-y-6'}`}>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 pl-3 rtl:pl-0 rtl:pr-3 flex items-center pointer-events-none">
              <svg
                className={`text-gray-400 transition-all duration-500 ease-in-out ${compact ? 'w-5 h-5' : 'w-6 h-6'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              id="keyword"
              name="keyword"
              onChange={handleKeywordChange}
              value={formik.values.keyword}
              placeholder={translations.searchPlaceholder}
              className={`w-full pl-10 rtl:pl-0 rtl:pr-10 pr-4 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-800 dark:text-white transition-all duration-500 ease-in-out ${
                compact ? 'py-3 text-base' : 'py-5 md:py-6 text-lg md:text-xl'
              } ${
                formik.errors.keyword
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
            {formik.errors.keyword && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                {formik.errors.keyword}
              </p>
            )}
          </div>

          {/* Search Button */}
          <button
            type="submit"
            disabled={isSearching}
            className={`px-8 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all duration-500 ease-in-out font-semibold whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed ${
              compact ? 'py-3 text-base' : 'py-5 md:py-6 text-lg'
            }`}
          >
            {isSearching
              ? (() => {
                  const count = Math.max(
                    keywordsFoundCount,
                    searchProgress?.keywordsFound ?? 0,
                  );
                  return currentLanguage === 'fa'
                    ? `${count.toLocaleString('fa-IR')} کلمه`
                    : `${count.toLocaleString('en-US')} keywords`;
                })()
              : translations.searchButton}
          </button>
        </div>

        {statusMessage && (
          <div className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-4 py-3 text-center">
            {statusMessage}
          </div>
        )}

        {/* Brands, Language and Country Selectors */}
        <div className="flex flex-col md:flex-row gap-4">
          {/* Brands Selector */}
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              {currentLanguage === 'fa' ? 'منبع' : 'Sources'}
            </label>
            <div dir={isRTL ? 'rtl' : 'ltr'}>
              <Select<BrandOption, true>
                id="brands"
                name="brands"
                options={brandOptions.filter((option) => option.value === 'google')}
                value={brandOptions.filter((option) => option.value === 'google')}
                onChange={() => {
                  formik.setFieldValue('brands', ['google']);
                }}
                isMulti
                isSearchable={false}
                isClearable={false}
                isDisabled
                placeholder={currentLanguage === 'fa' ? 'منبع را انتخاب کنید' : 'Select sources'}
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                maxMenuHeight={300}
                formatOptionLabel={({ label, icon, image }) => (
                  <div className="flex items-center gap-2">
                    {image ? (
                      <img 
                        src={image} 
                        alt={label} 
                        className="w-5 h-5 object-contain flex-shrink-0"
                        onError={(e) => {
                          // Fallback to emoji if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const emojiSpan = target.parentElement?.querySelector('.emoji-fallback') as HTMLElement;
                          if (emojiSpan) emojiSpan.style.display = 'inline';
                        }}
                      />
                    ) : null}
                    <span className={`flex items-center justify-center w-5 h-5 flex-shrink-0 ${image ? 'emoji-fallback hidden' : ''}`} style={{ fontSize: '20px', lineHeight: '1' }}>{icon}</span>
                    <span>{label}</span>
                  </div>
                )}
                theme={(selectTheme) => ({
                  ...selectTheme,
                  colors: {
                    ...selectTheme.colors,
                    primary: '#6e46e5',
                    primary75: '#8b6ce8',
                    primary50: '#a894eb',
                    primary25: '#c5b8ee',
                  },
                })}
              />
            </div>
          </div>

          {/* Language Selector */}
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              {translations.language || 'زبان'}
            </label>
            <div dir={isRTL ? 'rtl' : 'ltr'}>
              <Select<SelectOption>
                id="language"
                name="language"
                options={languageOptions}
                value={languageOptions.find(option => option.value === formik.values.language)}
                onChange={(selectedOption) => {
                  formik.setFieldValue('language', selectedOption?.value || 'fa');
                }}
                isSearchable
                isClearable={false}
                placeholder={translations.language || 'زبان را انتخاب کنید'}
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                maxMenuHeight={300}
                theme={(selectTheme) => ({
                  ...selectTheme,
                  colors: {
                    ...selectTheme.colors,
                    primary: '#6e46e5',
                    primary75: '#8b6ce8',
                    primary50: '#a894eb',
                    primary25: '#c5b8ee',
                  },
                })}
              />
            </div>
          </div>

          {/* Country Selector */}
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              {translations.country || 'کشور'}
            </label>
            <div dir={isRTL ? 'rtl' : 'ltr'}>
              <Select<SelectOption>
                id="country"
                name="country"
                options={countryOptions}
                value={countryOptions.find(option => option.value === formik.values.country)}
                onChange={(selectedOption) => {
                  formik.setFieldValue('country', selectedOption?.value || 'ir');
                }}
                isSearchable
                isClearable={false}
                placeholder={translations.country || 'کشور را انتخاب کنید'}
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                maxMenuHeight={300}
                theme={(selectTheme) => ({
                  ...selectTheme,
                  colors: {
                    ...selectTheme.colors,
                    primary: '#6e46e5',
                    primary75: '#8b6ce8',
                    primary50: '#a894eb',
                    primary25: '#c5b8ee',
                  },
                })}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SearchForm;


